export const OP_AUDIO_CHUNK = 0x01;
export const OP_END_UTTERANCE = 0x02;
export const OP_CANCEL = 0x03;
export const OP_TTS_CHUNK = 0x81;
export const OP_TTS_SENTENCE_END = 0x82;
export const OP_TTS_DONE = 0x83;

export type ServerFrame =
  | { kind: 'chunk'; sentence: number; seq: number; audio: ArrayBuffer }
  | { kind: 'sentenceEnd'; sentence: number }
  | { kind: 'done' };

/**
 * Spec §7 is big-endian, which is also what `DataView.getUint32(offset)`
 * defaults to. Returns null for anything unrecognised: a truncated frame or a
 * future opcode must not be mistaken for the end of the turn.
 */
export function decode(buf: ArrayBuffer): ServerFrame | null {
  if (buf.byteLength === 0) return null;
  const view = new DataView(buf);
  switch (view.getUint8(0)) {
    case OP_TTS_CHUNK:
      if (buf.byteLength < 9) return null;
      return {
        kind: 'chunk',
        sentence: view.getUint32(1),
        seq: view.getUint32(5),
        audio: buf.slice(9),
      };
    case OP_TTS_SENTENCE_END:
      if (buf.byteLength < 5) return null;
      return { kind: 'sentenceEnd', sentence: view.getUint32(1) };
    case OP_TTS_DONE:
      return { kind: 'done' };
    default:
      return null;
  }
}

/**
 * Whether a close means the conversation went mute under the learner.
 *
 * The server closes with 1000 only once the session's row has ended
 * (`audio_ws::run`), and that close can arrive before the `SessionEnded` that
 * explains it. No other ending the server makes — an idle timeout, a write
 * deadline, a restart — sends a close frame, and a browser reports a close
 * without one as 1006.
 */
export const closeIsDeath = (code: number): boolean => code !== 1000;

/**
 * Chunks held while the socket is still connecting — two seconds of audio.
 *
 * One chunk is one 128-frame render quantum decimated to 16 kHz, so it is
 * 2.67 ms and ~86 bytes, not the ~30 ms a coarser guess assumes.
 */
const MAX_PENDING_CHUNKS = 750;

export class AudioTransport {
  private ws: WebSocket;
  private pending: Uint8Array<ArrayBuffer>[] = [];
  private closed = false;
  private closeExpected = false;
  // Plain fields, not constructor parameter properties: `node --test` strips
  // types without transforming them, and a parameter property is syntax it
  // refuses outright.
  private readonly onFrame: (f: ServerFrame) => void;
  private readonly onDead: () => void;

  /**
   * `onDead` fires once, the first time this socket can no longer carry audio.
   *
   * It has to exist. The server tears the session's event stream down in the
   * same teardown that closes this socket, so both planes go quiet together
   * and a view that only listens to GraphQL learns nothing. Dropping frames
   * quietly — which is what this class did — leaves the microphone live, the
   * button reading "Hold to talk", and every word going nowhere. Spec §12 row
   * 4 asks for the session to survive a dropped socket; it does not ask for
   * the learner to be lied to about it.
   */
  constructor(url: string, token: string, onFrame: (f: ServerFrame) => void, onDead: () => void) {
    this.onFrame = onFrame;
    this.onDead = onDead;
    this.ws = new WebSocket(`${url}?token=${encodeURIComponent(token)}`);
    this.ws.binaryType = 'arraybuffer';
    this.ws.onmessage = (e) => {
      const frame = decode(e.data as ArrayBuffer);
      if (frame) this.onFrame(frame);
      else console.warn('audio socket: undecodable frame');
    };
    this.ws.onopen = () => {
      for (const f of this.pending) this.ws.send(f);
      this.pending = [];
    };
    this.ws.onerror = () => console.warn('audio socket error');
    // `onclose` fires for every ending — a clean close, a failed handshake, a
    // dropped connection — which is exactly the set the view has to react to,
    // less the one close that says the session is over, and any close at all
    // once the learner has asked for one (`expectClose`).
    this.ws.onclose = (e) => (!this.closeExpected && closeIsDeath(e.code) ? this.die() : this.release());
  }

  /**
   * The learner pressed End: the server is about to close this socket, and
   * however that close reaches the browser, it is not a dropped connection.
   *
   * The code alone cannot be trusted for this one ending. A connection reset
   * around the server's close frame makes the browser discard the frame and
   * report 1006 (see `CLOSE_HANDSHAKE_TIMEOUT` in `audio_ws/mod.rs`), and a
   * talk the learner ended with a "Disconnected" line over it is the bug
   * this exists for.
   */
  expectClose(): void {
    this.closeExpected = true;
  }

  /**
   * Close without telling the view: the session is over and nothing is lost,
   * or the provider is tearing this machine down for the next one. It is
   * also what gives the server its live-session slot back.
   */
  release(): void {
    this.closed = true;
    this.pending = [];
    this.ws.close();
  }

  /** Idempotent: a dropped socket must not tell the view once per frame. */
  private die(): void {
    if (this.closed) return;
    this.closed = true;
    this.pending = [];
    this.onDead();
  }

  /**
   * A `WebSocket` is `CONNECTING` for a round trip after construction. Dropping
   * chunks in that window loses the front of the utterance silently — the turn
   * still happens, with the first word missing — so they are held instead.
   *
   * Anything else means the socket is closing or gone, and the frame is
   * dropped without a verdict. `onclose` always follows and carries the code
   * that says which: deciding here instead read the server's own 1000 close
   * as a death, because Chrome is CLOSING from the server's close frame until
   * the TCP connection ends, and a hands-free microphone sends a chunk every
   * 2.67 ms of that window.
   */
  private send(frame: Uint8Array<ArrayBuffer>): void {
    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(frame);
    } else if (this.ws.readyState === WebSocket.CONNECTING) {
      if (this.pending.length < MAX_PENDING_CHUNKS) this.pending.push(frame);
    }
  }

  sendPcm(pcm: Int16Array): void {
    const frame = new Uint8Array(1 + pcm.byteLength);
    frame[0] = OP_AUDIO_CHUNK;
    frame.set(new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength), 1);
    this.send(frame);
  }

  endUtterance(): void {
    this.send(new Uint8Array([OP_END_UTTERANCE]));
  }

  /**
   * Barge-in — spec §7. `playedThrough` is the index of the last sentence
   * played to completion, so the server stores the reply only that far; `null`
   * is a learner who interrupted before any sentence finished.
   *
   * Big-endian, which is what `setUint32` writes without a third argument and
   * what the server's `u32::from_be_bytes` reads.
   */
  cancel(playedThrough: number | null): void {
    if (playedThrough === null) {
      this.send(new Uint8Array([OP_CANCEL]));
      return;
    }
    const frame = new Uint8Array(5);
    frame[0] = OP_CANCEL;
    new DataView(frame.buffer).setUint32(1, playedThrough);
    this.send(frame);
  }
}
